import React, { useEffect, useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';

import { itemOf, messageOf, resendCode, verifyCode } from '../api';
import { RESEND_SECONDS, SIGN_IN_PATHS } from '../constants';
import { readFlow, updateFlow } from '../flow';
import { Actions, BigButton, Check, Field, Frame, LinkRow, Messages, TextButton, Title } from '../ui';
import { useLive, useOutcome } from '../useOutcome';

const secondsLeft = (sentAt: number | null | undefined) =>
  sentAt ? Math.max(0, RESEND_SECONDS - Math.floor((Date.now() - sentAt) / 1000)) : RESEND_SECONDS;

/** The emailed 6-digit code (two-step sign-in). */
export const CodeScreen = () => {
  const history = useHistory();
  const [flow, setFlow] = useState(readFlow);
  const [code, setCode] = useState('');
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [wait, setWait] = useState(() => secondsLeft(flow.codeSentAt));
  const [busy, setBusy] = useState(false);
  const live = useLive();
  const outcome = useOutcome();

  useEffect(() => {
    if (wait <= 0) {
      return undefined;
    }
    const timer = setInterval(() => setWait((now) => Math.max(0, now - 1)), 1000);
    return () => clearInterval(timer);
  }, [wait > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!flow.challengeId) {
    return <Redirect to={SIGN_IN_PATHS.SIGN_IN} />;
  }
  const challengeId = flow.challengeId;
  const keepSignedIn = flow.keepSignedIn ?? true;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) {
      return;
    }
    const digits = code.replace(/\D/g, '');
    if (digits.length < 6) {
      setOk('');
      setError('Enter all 6 digits.');
      return;
    }
    setBusy(true);
    setError('');
    setOk('');
    const answer = await verifyCode({ challengeId, code: digits, rememberDevice: remember, keepSignedIn });
    const message = await outcome(answer, { keepSignedIn, email: flow.email });
    if (live.current) {
      setBusy(false);
      if (message) {
        setError(message);
      }
    }
  };

  const resend = async () => {
    if (wait > 0) {
      return;
    }
    setError('');
    setOk('');
    const answer = await resendCode(challengeId);
    if (!live.current) {
      return;
    }
    const result = itemOf(answer);
    if (result?.outcome === 'code-required') {
      setFlow(updateFlow({
        challengeId: result.challengeId || challengeId,
        codeSentTo: result.codeSentTo || flow.codeSentTo,
        codeSentAt: Date.now(),
      }));
      setCode('');
      setOk('New code sent. Only the newest code works.');
      setWait(RESEND_SECONDS);
    } else {
      setError(result?.message || messageOf(answer));
    }
  };

  const otherAccount = () => {
    updateFlow({ challengeId: null, codeSentTo: null, codeSentAt: null });
    history.push(SIGN_IN_PATHS.SIGN_IN);
  };

  return (
    <Frame>
      <Title
        icon="mark_email_unread"
        title="Check your email"
        sub={`We sent a 6-digit code to ${flow.codeSentTo || flow.email || 'your email'}. It works for 10 minutes.`}
      />
      <form onSubmit={submit} noValidate>
        <Field
          id="sx-code"
          label="Code"
          value={code}
          onChange={(value) => {
            setCode(value.replace(/[^0-9 ]/g, '').slice(0, 7));
            setError('');
          }}
          big
          bad={!!error}
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="000000"
          autoFocus
        />
        <Check checked={remember} onChange={setRemember} label="Don’t ask for a code on this device for 30 days" />
        <Messages error={error} ok={ok} />
        <Actions>
          <BigButton type="submit" aria-busy={busy}>Verify and sign in</BigButton>
        </Actions>
        <LinkRow>
          <TextButton onClick={resend} disabled={wait > 0}>
            {wait > 0 ? `Send a new code in 0:${String(wait).padStart(2, '0')}` : 'Send a new code'}
          </TextButton>
          <TextButton onClick={otherAccount}>Use a different account</TextButton>
        </LinkRow>
      </form>
    </Frame>
  );
};
